import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, RotateCcw, Send, Save, User, Bell } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";

const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";

export default function EmailTemplates() {
  const qc = useQueryClient();
  const { data: templates } = useQuery({ queryKey: ["email-templates"], queryFn: () => api.get("/email-templates").then((r) => r.data.templates) });
  const [activeKey, setActiveKey] = useState(null);
  const list = templates || [];
  const active = list.find((t) => t.key === activeKey) || list[0];
  const refresh = () => qc.invalidateQueries({ queryKey: ["email-templates"] });

  return (
    <div>
      <PageTitle title="Email Templates" subtitle="Customize the wording and branding of every automated email — no code required" />
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div className="space-y-4">
          <TemplateGroup title="Customer Emails" icon={User} items={list.filter((t) => t.category === "customer")} activeKey={active?.key} onSelect={setActiveKey} />
          <TemplateGroup title="Owner Alerts" icon={Bell} items={list.filter((t) => t.category === "owner")} activeKey={active?.key} onSelect={setActiveKey} />
        </div>
        {active && <Editor key={active.key} tpl={active} onChanged={refresh} />}
      </div>
    </div>
  );
}

function TemplateGroup({ title, icon: Icon, items, activeKey, onSelect }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-500"><Icon className="h-3.5 w-3.5" /> {title}</div>
      {items.map((t) => (
        <button key={t.key} onClick={() => onSelect(t.key)} data-testid={`tpl-item-${t.key}`}
          className={`w-full text-left px-4 py-2.5 text-sm border-b border-slate-50 hover:bg-slate-50 transition-colors ${activeKey === t.key ? "bg-electric/5 text-electric font-medium" : "text-navy"}`}>
          {t.name}{t.isCustom && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 align-middle">Edited</span>}
        </button>
      ))}
    </div>
  );
}

function Editor({ tpl, onChanged }) {
  const [form, setForm] = useState({ subject: tpl.subject, title: tpl.title, body: tpl.body, ctaText: tpl.ctaText || "" });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await api.put(`/email-templates/${tpl.key}`, form); toast.success("Template saved"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset this template to its default wording?")) return;
    setBusy(true);
    try { const r = await api.post(`/email-templates/${tpl.key}/reset`); setForm({ subject: r.data.subject, title: r.data.title, body: r.data.body, ctaText: r.data.ctaText || "" }); toast.success("Reset to default"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!testTo) return toast.error("Enter an email to send the test to");
    setBusy(true);
    try { const r = await api.post(`/email-templates/${tpl.key}/test`, { to: testTo }); toast.success(r.data.message); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const insertVar = (v) => set("body", `${form.body}{${v}}`);

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5 space-y-4" data-testid="template-editor">
      <div>
        <h3 className="font-heading font-semibold text-navy">{tpl.name}</h3>
        <p className="text-sm text-slate-500">{tpl.description}</p>
      </div>
      <div>
        <label className="text-sm text-slate-600 block mb-1">Subject line</label>
        <input className={input} value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="tpl-subject" />
      </div>
      <div>
        <label className="text-sm text-slate-600 block mb-1">Heading (inside the email)</label>
        <input className={input} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="tpl-title" />
      </div>
      <div>
        <label className="text-sm text-slate-600 block mb-1">Body (HTML allowed — use &lt;b&gt;, &lt;br&gt;)</label>
        <textarea className={`${input} h-auto py-2`} rows={5} value={form.body} onChange={(e) => set("body", e.target.value)} data-testid="tpl-body" />
      </div>
      <div>
        <label className="text-sm text-slate-600 block mb-1">Button text (leave empty for no button)</label>
        <input className={input} value={form.ctaText} onChange={(e) => set("ctaText", e.target.value)} data-testid="tpl-cta" />
      </div>
      {tpl.variables?.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-1.5">Available variables (click to insert):</p>
          <div className="flex flex-wrap gap-1.5">
            {tpl.variables.map((v) => (
              <button key={v} type="button" onClick={() => insertVar(v)} className="text-xs font-mono px-2 py-1 rounded bg-slate-100 hover:bg-electric hover:text-white text-slate-600 transition-colors" data-testid={`var-${v}`}>{`{${v}}`}</button>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
        <Button onClick={save} disabled={busy} className="bg-electric hover:bg-tech gap-1.5" data-testid="tpl-save"><Save className="h-4 w-4" /> Save</Button>
        <Button onClick={reset} disabled={busy} variant="outline" className="gap-1.5" data-testid="tpl-reset"><RotateCcw className="h-4 w-4" /> Reset to default</Button>
        <div className="flex gap-2 ml-auto">
          <input className={`${input} w-52`} type="email" placeholder="Send test to…" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="tpl-test-to" />
          <Button onClick={sendTest} disabled={busy} variant="outline" className="gap-1.5" data-testid="tpl-test-send"><Send className="h-4 w-4" /> Test</Button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 flex items-center gap-1"><Mail className="h-3 w-3" /> Test emails use sample data. In Resend test mode, delivery is limited to your verified address.</p>
    </div>
  );
}
